/**
 * EdgeLine — the signature mark of the Plans / Optimizer / Community pages:
 * one luminous indigo → green line, the shape of an expected-value curve,
 * that draws itself in once on mount and ends in a softly pulsing point.
 *
 * Web: an SVG path whose stroke is drawn with a CSS dash animation (compositor
 * only, no JS per frame). Native: the line renders fully drawn. Reduced
 * motion: drawn instantly, no pulse.
 *
 *   <EdgeLine height={160} variant="rise" />          // hero background
 *   <EdgeLine height={44} variant="flat" delay={300} /> // section divider
 */
import React, { useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Path, Defs, LinearGradient, Stop, Circle } from 'react-native-svg';
import { IS_WEB, useReducedMotion } from '../../utils/motion';

const PATHS = {
    // Flat noise on the left, then the edge appears and climbs.
    rise: 'M0 92 C 60 88, 110 96, 170 90 S 280 84, 330 88 S 430 92, 470 80 S 560 58, 620 52 S 720 30, 800 14',
    // Gentle wave for dividers.
    flat: 'M0 30 C 80 26, 140 34, 220 30 S 360 24, 440 28 S 600 34, 680 22 S 760 14, 800 12',
};
const END = { rise: [800, 14], flat: [800, 12] };
const VIEW_H = { rise: 104, flat: 44 };

// All coordinates are absolute x,y pairs, so scaling every pair maps the
// 800-wide design onto the measured box without distorting the stroke.
export function scalePath(d, sx, sy) {
    let i = 0;
    return d.replace(/-?\d+(\.\d+)?/g, (n) => {
        const v = Number(n) * (i++ % 2 === 0 ? sx : sy);
        return String(Math.round(v * 10) / 10);
    });
}

let _styleInjected = false;
function injectStyles() {
    if (_styleInjected || typeof document === 'undefined') return;
    _styleInjected = true;
    const el = document.createElement('style');
    el.setAttribute('data-bo-edge-line', '');
    el.textContent = `
[data-edge-line] path.bo-edge { stroke-dasharray: 1000; stroke-dashoffset: 1000; animation: boEdgeDraw 1600ms cubic-bezier(0.16,1,0.3,1) forwards; animation-delay: var(--bo-edge-delay, 0ms); }
[data-edge-line] path.bo-edge-glow { stroke-dasharray: 1000; stroke-dashoffset: 1000; animation: boEdgeDraw 1600ms cubic-bezier(0.16,1,0.3,1) forwards; animation-delay: var(--bo-edge-delay, 0ms); }
[data-edge-line] circle.bo-edge-dot { opacity: 0; transform-box: fill-box; transform-origin: center; animation: boEdgeDot 400ms ease-out forwards, boEdgePulse 2600ms ease-in-out 2000ms infinite; animation-delay: calc(var(--bo-edge-delay, 0ms) + 1350ms), calc(var(--bo-edge-delay, 0ms) + 1800ms); }
@keyframes boEdgeDraw { to { stroke-dashoffset: 0; } }
@keyframes boEdgeDot { to { opacity: 1; } }
@keyframes boEdgePulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: 0.55; } }
@media (prefers-reduced-motion: reduce) {
  [data-edge-line] path.bo-edge, [data-edge-line] path.bo-edge-glow { animation: none; stroke-dashoffset: 0; }
  [data-edge-line] circle.bo-edge-dot { animation: none; opacity: 1; }
}`;
    document.head.appendChild(el);
}

export default function EdgeLine({ height = 140, variant = 'rise', delay = 0, opacity = 1, style, strokeWidth = 2.5 }) {
    const reduced = useReducedMotion();
    if (IS_WEB) injectStyles();
    const id = useMemo(() => `boEdge${Math.random().toString(36).slice(2, 8)}`, []);
    const [box, setBox] = useState(null);
    const vh = VIEW_H[variant] || 104;
    const sx = box ? box.w / 800 : 1;
    const sy = box ? box.h / vh : 1;
    const d = scalePath(PATHS[variant] || PATHS.rise, sx, sy);
    const [ex, ey] = (END[variant] || END.rise);
    const animate = IS_WEB && !reduced;

    return (
        <View
            style={[styles.wrap, { height, opacity }, style]}
            onLayout={(e) => { const { width: w, height: h } = e.nativeEvent.layout; if (w > 0 && h > 0) setBox({ w, h }); }}
            pointerEvents="none"
            aria-hidden
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            {...(IS_WEB ? { dataSet: { edgeLine: animate ? 'on' : 'off' } } : {})}
        >
            {box ? (
            <Svg width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} style={IS_WEB ? { overflow: 'visible', '--bo-edge-delay': `${delay}ms` } : undefined}>
                <Defs>
                    <LinearGradient id={id} x1="0" y1="0" x2="1" y2="0">
                        <Stop offset="0" stopColor="#6366F1" stopOpacity="0.15" />
                        <Stop offset="0.45" stopColor="#8B5CF6" stopOpacity="0.85" />
                        <Stop offset="1" stopColor="#34D399" stopOpacity="1" />
                    </LinearGradient>
                </Defs>
                <Path d={d} pathLength={1000} fill="none" stroke={`url(#${id})`} strokeWidth={strokeWidth * 5} strokeOpacity={0.12} strokeLinecap="round" className={animate ? 'bo-edge-glow' : undefined} />
                <Path d={d} pathLength={1000} fill="none" stroke={`url(#${id})`} strokeWidth={strokeWidth} strokeLinecap="round" className={animate ? 'bo-edge' : undefined} />
                <Circle cx={ex * sx} cy={ey * sy} r={4.5} fill="#34D399" className={animate ? 'bo-edge-dot' : undefined} />
            </Svg>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: { width: '100%', overflow: 'visible' },
});
